import { Disclosure } from '@headlessui/react'
import { ChevronRightIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd'

import { CompletedStateMessage } from './CompletedStateMessage'
import { TaskItemV2 } from './TaskItemV2'
import { ChecklistItem } from '@/types/unlock'
import { ApplicationState } from '@/utils/checklistGroupMappings'

interface TaskListProps {
  tasks: ChecklistItem[]
  tasksToShow: ChecklistItem[]
  taskFilterOption: string
  isUpdatingSequence: boolean
  isLoadingApplication: boolean
  completeAllItems: () => void
  toggleTask: (task: ChecklistItem) => void
  handleDragEnd: (
    updateSequence: (params: any) => Promise<void>
  ) => (result: DropResult) => Promise<void>
  updateCheckListItemSequenceNo: any
  viewingStage: string
  applicationStage: string
  nextGroup?: ApplicationState
  goToNextStage: () => void
  hasEscalationsNotApproved: boolean
  isUpdating: boolean
  isLoadingUser: boolean
  user: any
  stageName: string
}

export const TaskList = ({
  tasks,
  tasksToShow,
  taskFilterOption,
  isUpdatingSequence,
  isLoadingApplication,
  completeAllItems,
  toggleTask,
  handleDragEnd,
  viewingStage,
  applicationStage,
  nextGroup,
  goToNextStage,
  hasEscalationsNotApproved,
  isUpdating,
  isLoadingUser,
  user,
  updateCheckListItemSequenceNo,
  stageName,
}: TaskListProps) => {
  if (tasks.length === 0) return null

  return (
    <>
      <div className='mx-4 mb-2 justify-between flex'>
        {tasksToShow.length && taskFilterOption === 'uncompleted' ? (
          <>
            <h3 className='inline-block font-bold text-[12px] text-blue-900'>
              Complete the following tasks
            </h3>
            <span
              onClick={completeAllItems}
              className='text-gray-400 inline-block text-end cursor-pointer text-[10px] hover:underline'
            >
              Mark all as completed
            </span>
          </>
        ) : null}
      </div>

      <Disclosure defaultOpen>
        {({ open }) => (
          <div className='card border shadow bg-base-100 m-3 mt-0'>
            <Disclosure.Button
              className={`card-title bg-green-300 p-3 rounded-t-box text-base font-normal transition-all ease-in-out ${
                !open ? 'rounded-b-box' : ''
              }`}
            >
              <div className='flex justify-between w-full'>
                <div className='flex gap-1'>
                  <ClipboardDocumentListIcon
                    className=''
                    width={25}
                    height={25}
                  />
                  {stageName}
                </div>
                <div className='flex gap-1 items-center'>
                  {!tasks.filter((task) => task.status !== 'done').length && (
                    <div className='text-xs badge badge-success text-white'>Completed</div>
                  )}
                  <ChevronRightIcon
                    width={20}
                    className={
                      open
                        ? '-rotate-90 transform transition-all'
                        : 'rotate-90 transform transition-all'
                    }
                  />
                </div>
              </div>
            </Disclosure.Button>
            <Disclosure.Panel>
              <div className='card-body gap-3 !p-0'>
                {!tasks.some((task) => task.status !== 'done') &&
                taskFilterOption === 'uncompleted' ? (
                  <CompletedStateMessage
                    viewingStage={viewingStage}
                    applicationStage={applicationStage}
                    nextGroup={nextGroup}
                    goToNextStage={goToNextStage}
                    hasEscalationsNotApproved={hasEscalationsNotApproved}
                    isLoadingApplication={isLoadingApplication}
                    isUpdating={isUpdating}
                    isLoadingUser={isLoadingUser}
                    user={user}
                  />
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd(updateCheckListItemSequenceNo)}>
                    <Droppable droppableId='droppable'>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          <>
                            {tasksToShow.length > 0 ? (
                              <>
                                {tasksToShow.map(
                                  (item: ChecklistItem, index) =>
                                    item.is_visible && (
                                      <TaskItemV2
                                        task={item}
                                        key={item.uuid}
                                        index={index}
                                        toggleTask={toggleTask}
                                        draggableDisabled={
                                          isUpdatingSequence || isLoadingApplication
                                        }
                                        lastItem={index === tasksToShow.length - 1}
                                      />
                                    )
                                )}
                                {provided.placeholder}
                              </>
                            ) : (
                              <div className='px-4 py-5 border-b justify-between bg-white rounded-b-box'></div>
                            )}
                          </>
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    </>
  )
}
